import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";

import type { Session } from "../session.ts";

/**
 * The top bar of `22 WP21 Merchant Portal.dc.html`: the scope chip, then the header kit.
 *
 * **The chip says a scope, not a tenant, and that is this console's one real departure from its
 * three siblings.** Every other console is signed in to one tenant and is required to say which;
 * this one reads `GET /v1/platform/audit-records`, whose own published description says results are
 * **not** restricted to the verified token's tenant. So a chip reading the merchant's own tenant
 * would name the one tenant the board is not about. The design agrees and goes further: its
 * `_rv()` computes `tenantIni: one ? 'EA' : 'ALL'` and `tenantName: one ? 'East Africa Mobility
 * Ltd' : 'All tenants'`, driven by a `Scope` control with two positions — and this console has no
 * second position, because nothing publishes `GET /v1/tenants` for it to switch to. Q-179.
 *
 * The geometry is that file's: a 32px square mark, the name and a mono second line inside a
 * `5px 10px 5px 5px` pill with a 14px radius and an inset 2px ring, with the three kit buttons
 * pushed to the far end by a growing spacer. Every number is read from its own `_rv()` and
 * `hdrUI()`, which it carries byte-identically with the other work-package files.
 *
 * The kit itself is design rule 3's four parts, and the measurements are `hdrUI()`'s, which every
 * work-package file carries byte-identically: 38px square buttons with a 10px radius, a 320px
 * popover 46px below the bar with a 14px radius, account rows 38px high at 13.5px, and the unread
 * badge at -5px from the top right with a 2px ring in the surface colour so it reads as lifted off
 * the button rather than clipped to it.
 *
 * One popover at a time, as the design's own toggle does. Escape closes whatever is open and a click
 * outside does the same — neither of which the design file needed to handle, because it is a
 * reference rather than something an administrator uses for a whole shift.
 */

const inbox = services.notifications__in_app_notification;

/**
 * The scope the bell spends, named here because naming it is what grants it.
 *
 * `tooling/scripts/lib/channel-scopes.mjs` derives each client's realm grant from **string literals
 * in the application's own source**, intersected with the published scope vocabulary — read from the
 * TypeScript AST, so a scope named only in a comment or a sentence does not count. Its docblock
 * states the limit plainly: "a screen that spends a scope without naming it — calling an operation
 * through a generated client and never mentioning the permission in its own source — contributes
 * nothing here, and the token would be short that scope."
 *
 * **The bell was exactly that, on every page of all three consoles.** It calls
 * `listInAppNotifications` through the generated client and named no permission, so the derivation
 * saw nothing, neither realm granted `in-app-notifications:read`, and the inbox answered 403 for
 * every merchant on every screen. Nothing caught it: the `catch` below is deliberate — a bell must
 * not take a board down — so the failure looked like an unreachable service rather than a missing
 * grant. Found by an adversarial review on 10 October, which also noted this was the only refusal on
 * these consoles that did not name its scope.
 *
 * The two existing bounds do not reach here, and that is why this is a constant rather than a note:
 * each console's `tests/page-routes.test.ts` checks that a *page's* declared requirements open one of
 * its owners, and the header kit is not a page.
 */
const INBOX_SCOPE = "in-app-notifications:read";

/** How many notifications the dropdown shows. The badge counts the inbox, not this page. */
const DROPDOWN_LIMIT = "10";

export interface HeaderKitProps {
  readonly session: Session;
  /** The page's own title, which is what the design makes the help popover's heading. */
  readonly helpTitle: string;
  /** Page tips. The design's first tip is the page's own description; see Q-03 and Q-20. */
  readonly tips: readonly string[];
  readonly transport: Transport;
  readonly onSignOut?: () => void;
  /** Opens and closes the drawer, below 640 where the design takes the rail out of the layout. */
  readonly onToggleNav?: () => void;
  readonly navOpen?: boolean;
}

type OpenPanel = "help" | "bell" | "account" | undefined;

interface InboxNotification {
  readonly notificationId: string;
  readonly title: string;
  readonly body: string;
  readonly state: string;
  readonly occurredAt: string;
}

/** The account menu, exactly as this design file lists it, in its order. */
const ACCOUNT_ITEMS = [
  { icon: "person", label: "My account" },
  { icon: "badge", label: "Profile" },
  { icon: "tune", label: "Preferences" },
  { icon: "keyboard", label: "Keyboard shortcuts" },
  { icon: "logout", label: "Sign out" },
] as const;

/**
 * The second line of the chip, and the half of it that has a source.
 *
 * The design's own values are `tenantName: 'Kilimani Fresh'` and
 * `tenantSub: 'mer_11 · on CityCab Market'` — **the merchant's name over the merchant's identifier
 * and the marketplace it trades on.** Neither half of that second line is quite available:
 *
 *   - `mer_11` is a short merchant code and nothing publishes one. The session carries a
 *     `merchant_id` claim, which is a uuid, and MER-01's listing would answer a name — the absence
 *     this portal's `Branches and hours` page is also blocked on.
 *   - `on CityCab Market` is the tenant's brand name, which R-07 measured as published to no
 *     shell by anything.
 *
 * So the chip draws the **tenant's** name where the design draws the merchant's, and the merchant
 * identifier beside this portal's own name in place of the marketplace. That is the truest thing
 * available in each position: a tenant name is a real name of something this session belongs to,
 * where a uuid in the first position would be a code a merchant does not recognise as themselves.
 * Recorded with the breadcrumb's identical departure in `main.tsx`.
 */
const SCOPE_SUB = "Corporate";

/**
 * The chip's second line, from `21 WP20 Corporate Portal.dc.html`'s own `tenantSub`.
 *
 * The design writes `Business account on CityCab · proposal`. The tenant's name is already drawn
 * beside this line, and `proposal` described the portal before ADR 0095 wrote the service behind
 * it, so what is left of that line is the part still true.
 *
 * **This file was the merchant portal's, unchanged, and read `session.merchantId`.** A live
 * corporate portal therefore told every visitor `Merchant · no merchant on this token`, under
 * the tenant's name, beside a rail whose brand also read `Merchant`. There was never a claim to
 * read: a corporate account is a COR-01 record rather than a token claim, and one person can
 * manage several, so no single account belongs in the chrome.
 */
const TENANT_SUB = "business account";

/**
 * Two letters for the chip's mark: the first of the first two words.
 *
 * `EA` for "East Africa Mobility Ltd". Deliberately not `session.initials`, which takes the first
 * and *last* words because it is a person's initials and would read `EL` here.
 *
 * Presentation rather than identity, so it lives beside the thing that draws it.
 */
function tenantMark(tenantName: string): string {
  const words = tenantName
    .split(/[\s._-]+/)
    .map((word) => word.trim())
    .filter((word) => /^[A-Za-z0-9]/.test(word));
  if (words.length === 0) return tenantName.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/** "2 min ago". The design writes relative times on notification rows rather than clock times. */
function relative(occurredAt: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(occurredAt)) / 1000));
  if (Number.isNaN(seconds)) return "";
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function HeaderKit({
  session,
  helpTitle,
  tips,
  transport,
  onSignOut,
  onToggleNav,
  navOpen,
}: HeaderKitProps): ReactNode {
  const [open, setOpen] = useState<OpenPanel>(undefined);
  const [notifications, setNotifications] = useState<readonly InboxNotification[] | undefined>(
    undefined,
  );
  const [unreadCount, setUnreadCount] = useState<number | undefined>(undefined);
  const [inboxFailed, setInboxFailed] = useState(false);
  const region = useRef<HTMLDivElement>(null);

  const toggle = useCallback((panel: Exclude<OpenPanel, undefined>) => {
    setOpen((current) => (current === panel ? undefined : panel));
  }, []);

  /**
   * The inbox, read once on mount for the badge and again whenever the dropdown opens.
   *
   * The count is the service's own, not a count of these rows: a page of ten would say "9" when
   * forty are waiting, and the number on the bell is the reason somebody opens it.
   */
  const load = useCallback(async () => {
    if (session.userId === undefined) return;
    if (!session.scopes.has(INBOX_SCOPE)) {
      // No copy is invented for this: the design draws one unreachable-inbox state and this is
      // it. Whether a scope refusal should say so is Q-172, recorded rather than guessed at.
      setInboxFailed(true);
      setUnreadCount(undefined);
      return;
    }
    try {
      const client = inbox.createInAppNotificationClient(transport);
      const page = await client.listInAppNotifications(
        session.tenantId,
        session.userId,
        undefined,
        DROPDOWN_LIMIT,
      );
      setNotifications(page.items);
      setUnreadCount(page.unreadCount);
      setInboxFailed(false);
    } catch {
      // The bell is the least important thing on any console screen, and an administrator working a
      // board does not need its failure. It says so in the dropdown and shows no badge.
      setInboxFailed(true);
      setUnreadCount(undefined);
    }
  }, [transport, session.tenantId, session.userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (open === "bell") void load();
  }, [open, load]);

  useEffect(() => {
    if (open === undefined) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(undefined);
    };
    const onPointer = (event: MouseEvent): void => {
      if (!region.current?.contains(event.target as Node)) setOpen(undefined);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  const now = Date.now();
  const badge = unreadCount !== undefined && unreadCount > 0;

  return (
    <div className="hdr" ref={region}>
      {onToggleNav === undefined ? null : (
        // Below 640 the design's rail leaves the layout and its top bar grows this. CSS decides,
        // because the width decides and the design measures the width. This file is one of the four
        // that put the threshold at 640; see Q-08.
        <button
          type="button"
          className="hdr__menu"
          aria-expanded={navOpen === true}
          aria-label={navOpen === true ? "Close menu" : "Open menu"}
          onClick={onToggleNav}
        >
          <span aria-hidden="true" className="hdr__glyph">
            menu
          </span>
        </button>
      )}

      {/*
        The tenant chip, which the design draws as a **merchant** chip and this draws as the truest
        thing available in each of its three positions — see this file's header for each.

        No brand mark and no brand colour: R-07 measured that nothing publishes a tenant's brand to
        a shell, and nothing publishes a merchant's either. The mark is the initials of the name
        beside it, which is what every other channel's chip does.
      */}
      <span className="hdr__tenant">
        <span aria-hidden="true" className="hdr__tenant-mark">
          {tenantMark(session.tenantName)}
        </span>
        <span className="hdr__tenant-text">
          <span className="hdr__tenant-name">{session.tenantName}</span>
          <span
            className="hdr__tenant-sub"
            title="A corporate account is COR-01's record rather than a claim on the token, and
              one person can manage several, so the chrome names none of them."
          >
            {`${SCOPE_SUB} · ${TENANT_SUB}`}
          </span>
        </span>
      </span>

      {/* The design's own growing spacer, which is what pushes the kit to the far end. */}
      <span aria-hidden="true" className="hdr__spacer" />

      <div className="hdr__kit">
        <button
          type="button"
          className="hdr__icon"
          aria-expanded={open === "help"}
          aria-haspopup="dialog"
          aria-label="Help for this page"
          onClick={() => toggle("help")}
        >
          <span aria-hidden="true" className="hdr__glyph">
            help
          </span>
        </button>

        <div className="hdr__slot">
          <button
            type="button"
            className="hdr__icon"
            aria-expanded={open === "bell"}
            aria-haspopup="dialog"
            aria-label={
              unreadCount === undefined ? "Notifications" : `Notifications, ${unreadCount} unread`
            }
            onClick={() => toggle("bell")}
          >
            <span aria-hidden="true" className="hdr__glyph">
              notifications
            </span>
            {badge ? (
              <span aria-hidden="true" className="hdr__badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            ) : null}
          </button>
        </div>

        <div className="hdr__slot">
          <button
            type="button"
            className="hdr__avatar"
            aria-expanded={open === "account"}
            aria-haspopup="menu"
            /*
             * `Account menu`, as all seven design files have it, rather than the merchant's name
             * appended to it. There is one account menu on the page, so the name disambiguates
             * nothing, and the panel it opens names the merchant in its first row — which is where
             * the design puts that information.
             */
            aria-label="Account menu"
            onClick={() => toggle("account")}
          >
            {session.initials}
          </button>
        </div>

        {open === "help" ? (
          <div className="hdr__pop" role="dialog" aria-label={`Help: ${helpTitle}`}>
            <p className="hdr__pop-title">{helpTitle}</p>
            <ul className="hdr__tips">
              {tips.map((tip) => (
                <li key={tip}>
                  <span aria-hidden="true" className="hdr__tip-glyph">
                    lightbulb
                  </span>
                  {tip}
                </li>
              ))}
            </ul>
            {/*
              The design's closing line is a link to a help centre. There is no help centre and no
              URL for one anywhere in the bundle, so it is drawn as the design draws it — that file's
              own markup is a `span`, not an anchor — and it does nothing. Recorded as Q-20.
            */}
            <p className="hdr__pop-more">Open the help centre</p>
          </div>
        ) : null}

        {open === "bell" ? (
          <div className="hdr__pop" role="dialog" aria-label="Notifications">
            <p className="hdr__pop-title">Notifications</p>
            {inboxFailed ? (
              <p className="hdr__pop-empty">
                The console cannot reach your notifications right now. Everything else on this page
                is unaffected.
              </p>
            ) : notifications === undefined ? (
              <p className="hdr__pop-empty">Loading</p>
            ) : notifications.length === 0 ? (
              <p className="hdr__pop-empty">Nothing for you yet.</p>
            ) : (
              <ul className="hdr__notifs">
                {notifications.map((notification) => (
                  <li
                    key={notification.notificationId}
                    className={
                      notification.state === "unread"
                        ? "hdr__notif hdr__notif--unread"
                        : "hdr__notif"
                    }
                  >
                    {/*
                      One glyph for every row. This file's three sample rows each carry a different
                      icon and colour, but they are sample data: no design defines a mapping from a
                      notification's category to an icon, and the inbox service publishes no category
                      vocabulary to map. Q-04.
                    */}
                    <span aria-hidden="true" className="hdr__notif-glyph">
                      notifications
                    </span>
                    <span className="hdr__notif-text">
                      <span className="hdr__notif-title">{notification.title}</span>
                      <span className="hdr__notif-when">
                        {relative(notification.occurredAt, now)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {open === "account" ? (
          <div className="hdr__pop" role="menu" aria-label="Account">
            {/*
              The design's identity block shows a name, a role and an email. The token carries the
              name and neither of the other two, so the tenant takes the second line instead — the
              one thing this console must always say. Q-05.
            */}
            <p className="hdr__who">
              <span aria-hidden="true" className="hdr__who-avatar">
                {session.initials}
              </span>
              <span className="hdr__who-text">
                <span className="hdr__who-name">{session.displayName}</span>
                <span className="hdr__who-tenant">{session.tenantName}</span>
              </span>
            </p>
            <ul className="hdr__acct">
              {ACCOUNT_ITEMS.map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    role="menuitem"
                    className={
                      item.label === "Sign out"
                        ? "hdr__acct-row hdr__acct-row--out"
                        : "hdr__acct-row"
                    }
                    onClick={() => {
                      setOpen(undefined);
                      if (item.label === "Sign out") onSignOut?.();
                    }}
                  >
                    <span aria-hidden="true" className="hdr__acct-glyph">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
